/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IMediaModel,
  LabelPlacement,
  MediaTransmissionStatus,
  TimeHelper,
} from "@bms/common";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import "./MediaTransmissionStatusLabel.scss";

export interface IMediaTransmissionStatusLabelProps {
  media?: IMediaModel;
  placement?: LabelPlacement;
}

export const MediaTransmissionStatusLabel = ({
  media,
  placement = LabelPlacement.None,
}: IMediaTransmissionStatusLabelProps) => {
  const { t } = useTranslation();

  const getMediaTransmissionStatus = useCallback(
    (media?: IMediaModel) => {
      if (!media || !media?.StartDateTime) {
        return null;
      }

      const currentDate = Date.now();

      if (
        media?.EndDateTime &&
        TimeHelper.isBefore(media.EndDateTime, currentDate)
      ) {
        return {
          transmissionStatus: MediaTransmissionStatus.ENDED,
          label: t("MEDIA_TRANSMISSION_STATUS_ENDED"),
        };
      }

      if (TimeHelper.isBefore(currentDate, media.StartDateTime)) {
        return {
          transmissionStatus: MediaTransmissionStatus.UPCOMING,
          label: TimeHelper.format(media.StartDateTime, "D. MMM HH:mm"),
        };
      }

      if (
        TimeHelper.isCurrentBetween(media.StartDateTime, media.EndDateTime) ||
        !media.EndDateTime
      ) {
        return {
          transmissionStatus: MediaTransmissionStatus.LIVE,
          label: t("MEDIA_TRANSMISSION_STATUS_LIVE"),
        };
      }
    },
    [t],
  );

  const labelStyle = useMemo<React.CSSProperties>(() => {
    switch (placement) {
      case LabelPlacement.TopLeft:
        return {
          top: "0.25rem",
          left: "0.25rem",
        };
      case LabelPlacement.TopRight:
        return {
          top: "0.25rem",
          right: "0.25rem",
        };
      case LabelPlacement.BottomLeft:
        return {
          bottom: "0.5rem",
          left: "0.25rem",
        };
      case LabelPlacement.BottomRight:
        return {
          bottom: "0.5rem",
          right: "0.25rem",
        };
      case LabelPlacement.Metadata:
        return {
          position: "relative",
        };
      default:
        return {
          display: "none",
        };
    }
  }, [placement]);

  const transitionStatus = getMediaTransmissionStatus(media);

  if (!transitionStatus) {
    return null;
  }

  return (
    <div
      className="MediaTransmissionStatusLabel"
      style={labelStyle}
      data-transmission-status={transitionStatus.transmissionStatus}
    >
      <span>{transitionStatus.label}</span>
    </div>
  );
};
